import { useEffect, useState } from 'react';
import { getEmployeeOfMonth, GetEmployeeOfMonthOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy } from 'lucide-react';
import { useAuth } from 'zite-auth-sdk';
import { format, parseISO } from 'date-fns';

type EOMType = GetEmployeeOfMonthOutputType;
const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'MMMM yyyy') : ''; } catch { return d || ''; } };

export default function EmployeeOfMonth() {
  const { user } = useAuth();
  const [data, setData] = useState<EOMType | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    getEmployeeOfMonth({}).then(setData).finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="px-6 py-8 max-w-2xl mx-auto space-y-4">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-48" />
      {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
    </div>
  );

  const current = data?.current;

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 py-6">
        <h1 className="text-xl font-bold text-foreground flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-500" /> Employee of the Month</h1>
        <p className="text-sm text-muted-foreground mt-1">Celebrating outstanding team members at The Rattlebone</p>
      </div>

      <div className="px-6 py-6 max-w-2xl mx-auto space-y-6">
        {current ? (
          <div className="border border-amber-500/30 bg-amber-500/5 rounded-2xl p-6 text-center">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider mb-3">🏆 {current.month}</p>
            {current.photoUrl ? (
              <img src={current.photoUrl} alt={current.employeeName}
                className="w-28 h-28 rounded-full object-cover border-4 border-amber-500/30 mx-auto mb-4" />
            ) : (
              <div className="w-28 h-28 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-12 h-12 text-amber-500" />
              </div>
            )}
            <h2 className="text-2xl font-bold text-foreground">{current.employeeName}</h2>
            {current.employeeId === user?.id && (
              <p className="text-primary font-semibold mt-1">🌟 That's you — congratulations!</p>
            )}
            {current.reason && <p className="text-sm text-muted-foreground mt-3 max-w-md mx-auto leading-relaxed">{current.reason}</p>}
            {current.voucherAmount > 0 && (
              <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-amber-500/10 border border-amber-500/20 rounded-full">
                <span className="text-sm font-semibold text-amber-700">🎁 £{current.voucherAmount} Employee Gift Voucher awarded</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12 border border-dashed border-border rounded-xl">
            <Trophy className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No Employee of the Month set yet</p>
          </div>
        )}

        {/* Hall of Fame */}
        {(data?.hallOfFame.length ?? 0) > 1 && (
          <div>
            <h2 className="text-sm font-bold text-foreground uppercase tracking-wider mb-3">🏛️ Hall of Fame</h2>
            <div className="space-y-3">
              {data?.hallOfFame.slice(1).map(entry => (
                <div key={entry.id} className="border border-border rounded-xl bg-card p-4 flex items-center gap-4">
                  {entry.photoUrl ? (
                    <img src={entry.photoUrl} alt={entry.employeeName} className="w-12 h-12 rounded-full object-cover border border-border shrink-0" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Trophy className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">{entry.employeeName}</p>
                    <p className="text-xs text-muted-foreground">{entry.month}</p>
                    {entry.reason && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{entry.reason}</p>}
                  </div>
                  {entry.employeeId === user?.id && <span className="text-xs text-primary font-medium shrink-0">You!</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
